import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  SheetClose,
  SheetFooter,
  SheetHeader,
} from '@/_components/ui'

import type { EditUserSchema } from '@/_schemas/update-user'
import { editUserSchema } from '@/_schemas/update-user'
import { ApiQueryError, mutations } from '@/features/_lib/react-query'
import { SendRecoveryPasswordEmailButton } from './send-recovery-password-email-button'

type EditUserSheetProps = {
  id: string
  name: string
  email: string
  companyId: string
  setIsSheetOpen: (isOpen: boolean) => void
}

export const EditUserSheet = ({
  id,
  name,
  email,
  companyId,
  setIsSheetOpen,
}: EditUserSheetProps) => {
  const queryClient = useQueryClient()
  const updateUser = useMutation(mutations.users.update(queryClient))

  const form = useForm<EditUserSchema>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      name,
      email,
    },
    disabled: updateUser.isPending,
  })

  useEffect(() => {
    form.setValue('id', id)
    form.setValue('companyId', companyId)
  }, [id, companyId, form])

  const onSubmit = (data: EditUserSchema) => {
    updateUser.mutate(
      {
        id: data.id,
        name: data.name,
        email: data.email,
      },
      {
        onSuccess: () => {
          setIsSheetOpen(false)
          toast.success('Usuário atualizado com sucesso!')
        },
        onError: (error) => {
          toast.error('Erro ao atualizar o usuário', {
            description:
              error instanceof ApiQueryError
                ? (error.apiError.detail ?? error.message)
                : 'Erro inesperado',
          })
        },
      },
    )
  }

  return (
    <div>
      <SheetHeader>
        <h2 className="px-5 text-xl font-bold">{name}</h2>
        <div className="flex flex-col gap-2 border-y border-solid border-brand-grayscale-3 px-5 py-5">
          <div className="flex justify-between">
            <SendRecoveryPasswordEmailButton name={name} email={email} />
          </div>
        </div>
      </SheetHeader>

      <div className="flex flex-col p-5">
        <h3 className="font-semibold text-brand-grayscale-12">Informações Básicas</h3>

        <Form {...form}>
          <form className="space-y-5 py-5" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite o nome" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite o e-mail" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline">Cancelar</Button>
              </SheetClose>
              <Button type="submit" disabled={updateUser.isPending}>
                {updateUser.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </div>
    </div>
  )
}
